// src/features/websites/components/StudioPreviewDialog.tsx
import {
  Article as ArticleIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  MusicNote as MusicIcon,
  Pause as PauseIcon,
  Person as PersonIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography
} from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/endPoints';
import { ListenerProfile } from '../../../types/studio.types';
import { generateStudioPreview } from '../api-studio';

// Define the interface for preview data
interface PreviewData {
  article: {
    id: string;
    headline: string;
    publishDate: string;
    content: string;
    author?: string;
  };
  script?: {
    text: string;
    audioUrl?: string;
  };
  endUser?: ListenerProfile;
  status: 'loading' | 'ready' | 'error';
  error?: string;
}

interface StudioPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  websiteId: string;
  studioConfig: any;
}

const StudioPreviewDialog: React.FC<StudioPreviewDialogProps> = ({
  open,
  onClose,
  websiteId,
  studioConfig,
}) => {
  const [previewData, setPreviewData] = useState<PreviewData>({
    article: {
      id: '',
      headline: '',
      publishDate: '',
      content: '',
    },
    status: 'loading',
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const fetchPreview = useCallback(async () => {
    try {
      setPreviewData(prev => ({
        ...prev,
        status: 'loading',
      }));

      // Step 1: Fetch the most recent article for this website
      const articlesResponse = await apiClient.get(
        API_ENDPOINTS.PUBLISHER.ARTICLES.BY_WEBSITE(websiteId),
        {
          params: {
            limit: 1,
            sort: 'publishDate:desc',
            status: 'published'
          }
        }
      );

      if (!articlesResponse.data || articlesResponse.data.length === 0) {
        setPreviewData(prev => ({
          ...prev,
          status: 'error',
          error: 'No published articles found for this website. Please publish at least one article to preview the studio configuration.',
        }));
        return;
      }

      const article = articlesResponse.data[0];

      // Step 2: Generate script and audio preview
      const previewResponse = await generateStudioPreview(
        websiteId,
        article.id,
        studioConfig,
        studioConfig.endUserProfile,
      );

      setPreviewData({
        article: {
          id: article.id,
          headline: article.headline,
          publishDate: article.publishDate,
          content: article.content,
          author: article.author || 'Unknown',
        },
        script: previewResponse.data.script,
        endUser: studioConfig.endUserProfile,
        status: 'ready',
      });

    } catch (error) {
      console.error('Error generating studio preview:', error);
      setPreviewData(prev => ({
        ...prev,
        status: 'error',
        error: 'Failed to generate preview. Please try again or check your configuration.',
      }));
    }
  }, [websiteId, studioConfig]);

  // Fetch article and generate preview on open
  useEffect(() => {
    if (open && websiteId) {
      fetchPreview();
    }

    // Clean up on close
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [open, websiteId, audioElement, fetchPreview]);

  // Create and manage the audio element
  useEffect(() => {
    if (!audioElement) {
      const newAudioElement = new Audio();
      newAudioElement.addEventListener('ended', handleEnded);
      setAudioElement(newAudioElement);
    }

    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.removeEventListener('ended', handleEnded);
      }
    };
  }, [audioElement, handleEnded]);

  // Update audio source when script changes
  useEffect(() => {
    if (audioElement && previewData.script?.audioUrl) {
      audioElement.src = previewData.script.audioUrl;
    }
  }, [previewData.script?.audioUrl, audioElement]);

  const togglePlayback = () => {
    if (!audioElement || !previewData.script?.audioUrl) return;

    if (isPlaying) {
      audioElement.pause();
    } else {
      audioElement.play();
    }

    setIsPlaying(!isPlaying);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  // Truncate text for display
  const truncateText = (text: string, maxLength: number = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Studio Preview</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {previewData.status === 'loading' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Generating preview with your studio configuration...
            </Typography>
          </Box>
        ) : previewData.status === 'error' ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>
              Error
            </Typography>
            <Typography variant="body1">
              {previewData.error || 'Failed to generate preview. Please try again.'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} /> End-User Profile Used for Preview
                </Typography>

                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1
                }}>
                  <Typography variant="body1">
                    <strong>Name:</strong> {previewData.endUser?.name || 'Not specified'}
                  </Typography>

                  <Typography variant="body1">
                    <strong>Location:</strong> {previewData.endUser?.location || 'Not specified'}
                  </Typography>

                  <Typography variant="body1">
                    <strong>Interests:</strong> {
                      previewData.endUser?.interests?.length
                        ? previewData.endUser.interests.join(', ')
                        : 'None specified'
                    }
                  </Typography>

                  <Typography variant="body1">
                    <strong>Preferred Content:</strong> {
                      previewData.endUser?.preferredContentTypes?.length
                        ? previewData.endUser.preferredContentTypes.join(', ')
                        : 'None specified'
                    }
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Article Preview Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <ArticleIcon sx={{ mr: 1 }} /> Article Used for Preview
                </Typography>

                <Typography variant="h6" gutterBottom>
                  {previewData.article.headline}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {previewData.article.author} • {formatDate(previewData.article.publishDate)}
                </Typography>

                <Typography variant="body1">
                  {truncateText(previewData.article.content, 300)}
                </Typography>
              </Paper>
            </Grid>

            {/* Co-Host Information Section */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} /> Co-Hosts
                </Typography>

                <List>
                  {studioConfig.coHosts.map((host: any, index: number) => (
                    <ListItem key={index} alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar alt={host.name} sx={{ bgcolor: index === 0 ? 'primary.main' : 'secondary.main' }}>
                          {host.name.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={host.name}
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.primary">
                              {host.voiceType === 'castingDirector' ? 'Using Casting Director' : 'Custom Voice'}
                            </Typography>
                            <br />
                            {truncateText(host.background, 100)}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Style Information Section */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <DescriptionIcon sx={{ mr: 1 }} /> Editorial Style
                </Typography>

                <Typography variant="body2" paragraph>
                  <strong>Attribution:</strong> {studioConfig.newsAttribution || 'Not specified'}
                </Typography>

                <Typography variant="body2">
                  <strong>Style:</strong> {truncateText(studioConfig.editorialStyle, 200) || 'Not specified'}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <MusicIcon sx={{ mr: 1, fontSize: '1rem' }} /> Audio Elements
                  </Typography>
                  <Typography variant="body2">
                    {studioConfig.introMusic ? 'Custom intro music configured' : 'Using default intro music'}
                    <br />
                    {studioConfig.outroMusic ? 'Custom outro music configured' : 'Using default outro music'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Generated Script & Audio Player Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                  Generated Script
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, mb: 3, maxHeight: 300, overflow: 'auto', bgcolor: 'action.hover' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {previewData.script?.text || 'No script generated yet.'}
                  </Typography>
                </Paper>

                {previewData.script?.audioUrl && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={isPlaying ? <PauseIcon /> : <PlayIcon />}
                      onClick={togglePlayback}
                      size="large"
                    >
                      {isPlaying ? 'Pause' : 'Play Preview'}
                    </Button>

                    <Typography variant="body2" color="text.secondary">
                      Listen to how your content will sound with this configuration
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudioPreviewDialog;